import {
  Box,
  Button,
  DialogContent,
  DialogTitle,
  DialogActions,
  Grid,
  SelectChangeEvent,
  TextField,
  Select,
  MenuItem,
  Dialog,
  Typography,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import greyDialogTitleSx from '../Dialog/greyDialogTitleSx';
import React, { FC, useEffect, useState } from "react";
import HighlightOffTwoToneIcon from '@mui/icons-material/HighlightOffTwoTone';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";

import {
  fetchDocumentSettingById,
  addDocumentSetting,
  updateDocumentSetting,
} from "../../store/documentSettingStore/documentSettingThunk";
import { fetchDocumentTypes } from "../../store/documentTypeStore/documentTypeThunk";
import { fetchTemplates } from '../../store/invoiceStore/invoiceThunk';
import { IOptions } from "../FormFields/CustomSelect";
import { FormProvider, useForm } from "react-hook-form";
import { IDocumentSettingFormInputs, DocNumberFormat, PrefixSuffixType, getDateFormatOptions, resolveDateFormatPreview } from "../../utils/documentSetting.types";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ApiStatus } from "../../utils/api.types";
import LoadingButton from "@mui/lab/LoadingButton";

interface DialogDocumentSettingFormProps {
  title?: string;
  documentSettingId?: string;
  accountId?: string;
  open: boolean;
  onClose: (k: any) => void;
  setDocumentType?: string;
}

const DialogDocumentSettingForm: FC<DialogDocumentSettingFormProps> = ({
  title,
  onClose,
  documentSettingId,
  accountId,
  open,
  setDocumentType
}) => {
  const dispatch = useAppDispatch();
  const params = useParams();
  const [documentTypeOptions, setDocumentTypeOptions] = useState<IOptions[]>([]);
  const [documentTypeValue, setDocumentTypeValue] = useState("");
  // Predefined additional fields for the default template. Kept outside RHF because
  // it is a variable-length list, not fixed inputs.
  const [additionalFieldDefaults, setAdditionalFieldDefaults] = useState<any[]>([]);
  const [tagsMandatory, setTagsMandatory] = useState(false);

  const [templateOptions, setTemplateOptions] = useState<IOptions[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");

  const [documentSettingData, setDocumentSettingData] = useState<any>();
  const [numberLengthValue, setNumberLengthValue] = useState<any>();
  const [sequenceValue, setSequenceValue] = useState<any>();

  // Prefix/Suffix state
  const [docNumberFormat, setDocNumberFormat] = useState<DocNumberFormat>('none');
  const [prefixType, setPrefixType] = useState<PrefixSuffixType>('custom');
  const [suffixType, setSuffixType] = useState<PrefixSuffixType>('custom');
  const [prefixDateFormat, setPrefixDateFormat] = useState<string>('');
  const [suffixDateFormat, setSuffixDateFormat] = useState<string>('');
  const [prefixSeparator, setPrefixSeparator] = useState<string>('-');
  const [suffixSeparator, setSuffixSeparator] = useState<string>('-');

  // Schema to validate account form.
  const schema = yup.object().shape({
    documentType: yup.string().required("Document type is required."),
    docNumberPrefix: yup.string(),
    footer1: yup.string().required("Footer 1 length is required."),
    footer2: yup.string().required("Footer 2 length is required."),
    docNumberLength: yup
      .number()
      .typeError('Document length is required and should be a number.')
      .required("Document length is required."),
    docSequenceNumber: yup.number()
      .typeError('Sequence number is required.')
      .required("Sequence number is required."),
  });

  //form
  const methods = useForm<IDocumentSettingFormInputs>({
    mode: "onTouched",
    resolver: yupResolver(schema),
  });

  const { status, documentSettings } = useAppSelector((state) => state.documentSettings);

  useEffect(() => {
    const getDocumentSettingDetails = async () => {
      if (documentSettingId) {
        const bd: any = await dispatch(
          fetchDocumentSettingById(documentSettingId)
        );

        setDocumentSettingData(bd.payload);
        setNumberLengthValue(bd.payload.docNumberLength);
        setSequenceValue(bd.payload.docSequenceNumber);

        methods.setValue("docNumberPrefix", bd.payload.docNumberPrefix);
        methods.setValue("docSequenceNumber", bd.payload.docSequenceNumber);
        methods.setValue("docNumberLength", bd.payload.docNumberLength);
        methods.setValue("termsAndConditions", bd.payload.termsAndConditions);
        methods.setValue("paymentDetails", bd.payload.paymentDetails);
        methods.setValue("paymentTermDays", bd.payload.paymentTermDays);
        methods.setValue("notes", bd.payload.notes);
        methods.setValue("footer1", bd.payload.footer1);
        methods.setValue("footer2", bd.payload.footer2);
        methods.setValue("smsAndWhatsAppText", bd.payload.smsAndWhatsAppText);
        methods.setValue("emailSubject", bd.payload.emailSubject);
        methods.setValue("emailText", bd.payload.emailText);
        methods.setValue("documentType", bd.payload.documentType?._id || "");
        setDocumentTypeValue(bd.payload.documentType?._id || "");
        methods.setValue("defaultPdfTemplate", bd.payload.defaultPdfTemplate);
        setSelectedTemplate(bd.payload.defaultPdfTemplate?._id || "");

        // Load prefix/suffix settings
        setDocNumberFormat(bd.payload.docNumberFormat || 'prefix');
        setPrefixType(bd.payload.prefixType || 'custom');
        setSuffixType(bd.payload.suffixType || 'custom');
        setPrefixDateFormat(bd.payload.prefixDateFormat || '');
        setSuffixDateFormat(bd.payload.suffixDateFormat || '');
        setPrefixSeparator(bd.payload.prefixSeparator ?? '-');
        setSuffixSeparator(bd.payload.suffixSeparator ?? '-');
        methods.setValue("docNumberSuffix", bd.payload.docNumberSuffix || '');
        setAdditionalFieldDefaults(bd.payload.additionalFieldDefaults || []);
        setTagsMandatory(!!bd.payload.tagsMandatory);
      } else {
        methods.setValue("footer1", "Powered by wareozo");
        methods.setValue("footer2", "Powered by wareozo");
        methods.setValue("paymentDetails", "Net-30");
      }
    };

    getDocumentSettingDetails();
  }, [documentSettingId, dispatch, methods]);

  useEffect(() => {
    const getDocumentTypes = async () => {
      if (!documentTypeOptions.length) {
        const data: any = await dispatch(fetchDocumentTypes());

        if (setDocumentType && !documentSettingId) {
          let obj = data.payload.find((o: { name: string; }) => o.name.toLowerCase() === setDocumentType);
          console.log(obj._id);
          setDocumentTypeValue(obj._id);
        }

        // In add mode, hide document types that already have a settings row for
        // this account (one settings entry per type). In edit mode keep them all —
        // the field is disabled and must still render its current type.
        const usedTypeIds = documentSettingId
          ? []
          : (documentSettings || []).map((s: any) => s.documentType?._id || s.documentType);

        setDocumentTypeOptions(
          data.payload
            .filter((c: any) => !usedTypeIds.includes(c._id))
            .map((c: any) => {
              return { label: c.name, value: c._id };
            })
        );
      }
    };
    if (open) {
      getDocumentTypes();
    }
  }, [open, dispatch, documentTypeOptions, documentSettingId, setDocumentType]);

  // User-built templates belong to one voucher, so the list is reloaded for the selected document type.
  const documentTypeName = documentTypeOptions.find((o) => o.value === documentTypeValue)?.label;
  useEffect(() => {
    let active = true;
    const getTemplates = async () => {
      const data: any = await dispatch(fetchTemplates(documentTypeName));
      if (!active) return;
      const options: IOptions[] = (data.payload || []).map((c: any) => ({ label: c.template, value: c._id }));
      setTemplateOptions(options);
      // A template picked for the previous document type may not exist for this one.
      const current = methods.getValues("defaultPdfTemplate") as any;
      const currentId = current?._id || current;
      if (documentTypeName && currentId && !options.some((o) => o.value === currentId)) {
        methods.setValue("defaultPdfTemplate", "");
        setSelectedTemplate("");
      }
    };

    if (open) {
      getTemplates();
    }
    return () => { active = false; };
  }, [open, dispatch, documentTypeName, methods]);

  const updateDocumentSettingDetails = async (e: any) => {
    if (documentSettingId) {
      e.account = documentSettingData?.account || '';

      const resData = await dispatch(
        updateDocumentSetting({
          documentSettingPayload: e,
          id: documentSettingId,
        })
      );

      const resMsg = resData.payload?.message?.toUpperCase();
      if (resMsg && resMsg.includes('SUCCESS')) {
        onClose(true);
      }
    }
  };

  const createDocumentSettingDetails = async (e: any) => {
    let _accountId = params.accountId || accountId;
    const resData = await dispatch(
      addDocumentSetting({ ...e, ...{ account: _accountId } })
    );

    if (status === ApiStatus.SUCCESS) {
      onClose(true);
    }
  };

  const onSubmit = (e: any) => {
    e.docSequenceNumber = sequenceValue;
    e.docNumberFormat = docNumberFormat;
    e.prefixType = prefixType;
    e.suffixType = suffixType;
    e.prefixDateFormat = prefixDateFormat;
    e.suffixDateFormat = suffixDateFormat;
    e.prefixSeparator = prefixSeparator;
    e.suffixSeparator = suffixSeparator;
    e.additionalFieldDefaults = additionalFieldDefaults.filter((f: any) => (f.label || '').trim() !== '');
    e.tagsMandatory = tagsMandatory;

    if (documentSettingId) {
      updateDocumentSettingDetails(e);
      return;
    }

    createDocumentSettingDetails(e);
  };

  const handleSequenceOnChange = (ev: any) => {
    let dVal = '';
    if (numberLengthValue < ev.target.value.length) {
      let temp = ev.target.value.length - numberLengthValue;
      let temp2 = ev.target.value.substring(temp);
      setSequenceValue(temp2);
      dVal = temp2;
    } else {
      setSequenceValue(ev.target.value);
      dVal = ev.target.value;
    }

    methods.setValue("docSequenceNumber", dVal, { shouldValidate: true });
  };

  const handleSequenceOnBlur = (ev: any) => {
    if (numberLengthValue >= ev.target.value.length) {
      let newSequenceValue = String(1).padStart(
        numberLengthValue,
        "0"
      );
      setSequenceValue(newSequenceValue);

      methods.setValue("docSequenceNumber", newSequenceValue, { shouldValidate: true });
    }
  };

  const handleNumberLengthChange = (e: any) => {
    setNumberLengthValue(e.target.value);
    const seq = e.target.value ? String(1).padStart(Number(e.target.value), "0") : "";
    setSequenceValue(seq);
    methods.setValue("docSequenceNumber", seq, { shouldValidate: true });
    methods.setValue("docNumberLength", e.target.value, { shouldValidate: true });
  };

  const readOnly = (documentSettingId && !open) ? true : false;
  const inputSx = { width: '100%', mt: 0.5, borderRadius: '8px!important' };
  const selectSx = {
    width: '100%',
    mt: 0.5,
    backgroundColor: '#fff',
    borderRadius: '10px',
    padding: '8px 12px',
    '& .MuiSelect-select': { padding: '0', display: 'flex', alignItems: 'center' },
  };
  const errorText = (message?: string) => message
    ? <p style={{ color: "#c33b3b", fontSize: '12px', margin: '4px 0 0 0' }}>{message}</p>
    : null;

  const textField = (name: keyof IDocumentSettingFormInputs, label: string, placeholder: string, extra: any = {}) => (
    <>
      <h5 className='c-label'>{label}</h5>
      <TextField
        placeholder={placeholder}
        {...methods.register(name)}
        size='small'
        className='c-text-field-4'
        variant="standard"
        sx={extra.multiline ? { ...inputSx, height: 'auto !important', pb: '5px' } : inputSx}
        InputProps={{ disableUnderline: true, readOnly }}
        {...extra}
      />
      {errorText((methods.formState.errors as any)[name]?.message)}
    </>
  );

  const section = (heading: string) => (
    <Grid item xs={12}>
      <Box sx={{ borderBottom: '1px solid #e4e4e4', pb: 1, mb: 0.5 }}>
        <Typography sx={{ fontWeight: 600, fontSize: '15px', color: '#1a1a1a' }}>{heading}</Typography>
      </Box>
    </Grid>
  );

  const prefixSuffixRow = (
    label: string,
    type: PrefixSuffixType,
    setType: (v: PrefixSuffixType) => void,
    textName: 'docNumberPrefix' | 'docNumberSuffix',
    textPlaceholder: string,
    dateFormat: string,
    setDateFormat: (v: string) => void,
    separator: string,
    setSeparator: (v: string) => void,
  ) => (
    <>
      <Grid item xs={12} md={4}>
        <h5 className='c-label'>{label} Type</h5>
        <Select value={type} onChange={(e: SelectChangeEvent<any>) => setType(e.target.value as PrefixSuffixType)} size='small' sx={selectSx}>
          <MenuItem value="custom">Custom Text</MenuItem>
          <MenuItem value="date">Date Format</MenuItem>
        </Select>
      </Grid>
      <Grid item xs={12} md={4}>
        {type === 'custom' ? textField(textName, `Document ${label}`, textPlaceholder) : (
          <>
            <h5 className='c-label'>Date Format</h5>
            <Select value={dateFormat} onChange={(e: SelectChangeEvent<any>) => setDateFormat(e.target.value)} size='small' sx={selectSx}>
              {getDateFormatOptions().map((opt) => (
                <MenuItem value={opt.value} key={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </>
        )}
      </Grid>
      <Grid item xs={12} md={4}>
        <h5 className='c-label'>{label} Separator</h5>
        <TextField placeholder='-' className='c-text-field-4' value={separator}
          onChange={(e) => setSeparator(e.target.value)} variant="standard"
          sx={inputSx} size='small' InputProps={{ disableUnderline: true }}
        />
      </Grid>
    </>
  );

  const previewText = () => {
    const seq = sequenceValue || '001';
    let prefix = '';
    let suffix = '';
    if (docNumberFormat === 'prefix' || docNumberFormat === 'both') {
      prefix = prefixType === 'date' && prefixDateFormat ? resolveDateFormatPreview(prefixDateFormat) + prefixSeparator : (methods.getValues('docNumberPrefix') || '') + (methods.getValues('docNumberPrefix') ? prefixSeparator : '');
    }
    if (docNumberFormat === 'suffix' || docNumberFormat === 'both') {
      suffix = suffixType === 'date' && suffixDateFormat ? suffixSeparator + resolveDateFormatPreview(suffixDateFormat) : (methods.getValues('docNumberSuffix') ? suffixSeparator : '') + (methods.getValues('docNumberSuffix') || '');
    }
    return prefix + seq + suffix;
  };

  const updateFieldDefault = (index: number, key: string, value: string) => {
    const rows = [...additionalFieldDefaults];
    rows[index] = { ...rows[index], [key]: value };
    setAdditionalFieldDefaults(rows);
  };

  const handleCancel = () => {
    methods.reset();
    onClose(false);
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose(false)}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { width: '100%', maxWidth: '1000px', maxHeight: '92vh' } }}
    >
      <FormProvider {...methods}>
        {(!documentSettingId || (documentSettingId && documentSettingData)) && (
          <Box
            component="form"
            onSubmit={methods.handleSubmit(onSubmit)}
            noValidate
            sx={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}
          >
            <DialogTitle sx={greyDialogTitleSx}>
              <Typography variant="h5">{documentSettingId ? "Update Document Settings" : "Add Document Settings"}</Typography>
            </DialogTitle>

            <DialogContent sx={{ flex: 1, overflow: "auto", px: 3, pt: '20px !important', pb: 3 }}>
              <Grid container columnSpacing={2.5} rowSpacing={2}>
                {section('Basic Settings')}
                <Grid item xs={12} md={4}>
                  <h5 className='c-label'>Document Type *</h5>
                  {documentTypeOptions?.length && (
                    <Select
                      {...methods.register('documentType')}
                      value={documentTypeValue ? documentTypeValue : ''}
                      disabled={documentSettingId ? true : false}
                      onChange={(event: SelectChangeEvent<any>) => {
                        methods.setValue("documentType", event.target.value, { shouldValidate: true });
                        setDocumentTypeValue(event.target.value);
                      }}
                      displayEmpty
                      renderValue={(value: any) => documentTypeOptions.find((o) => o.value === value)?.label || <span style={{ color: '#9e9e9e' }}>Select document type</span>}
                      size='small'
                      sx={selectSx}
                    >
                      {documentTypeOptions.map((option) =>
                        <MenuItem value={option.value} key={option.value}>{option.label}</MenuItem>
                      )}
                    </Select>
                  )}
                  {errorText(methods.formState.errors.documentType?.message)}
                </Grid>
                <Grid item xs={12} md={4}>
                  <h5 className='c-label'>Default PDF Template</h5>
                  {templateOptions?.length && (
                    <Select
                      {...methods.register('defaultPdfTemplate')}
                      value={selectedTemplate ? selectedTemplate : ''}
                      onChange={(event: SelectChangeEvent<any>) => {
                        methods.setValue("defaultPdfTemplate", event.target.value, { shouldValidate: true });
                        setSelectedTemplate(event.target.value);
                      }}
                      displayEmpty
                      renderValue={(value: any) => templateOptions.find((o) => o.value === value)?.label || <span style={{ color: '#9e9e9e' }}>Select template</span>}
                      size='small'
                      sx={selectSx}
                    >
                      {templateOptions.map((option) =>
                        <MenuItem value={option.value} key={option.value}>{option.label}</MenuItem>
                      )}
                    </Select>
                  )}
                  {errorText((methods.formState.errors as any).defaultPdfTemplate?.message)}
                </Grid>
                <Grid item xs={12} md={4}>
                  <h5 className='c-label'>Number Format</h5>
                  <Select value={docNumberFormat} onChange={(e: SelectChangeEvent<any>) => setDocNumberFormat(e.target.value as DocNumberFormat)} size='small' sx={selectSx}>
                    <MenuItem value="none">Sequence Number Only</MenuItem>
                    <MenuItem value="prefix">Prefix Only</MenuItem>
                    <MenuItem value="suffix">Suffix Only</MenuItem>
                    <MenuItem value="both">Prefix + Suffix</MenuItem>
                  </Select>
                </Grid>

                {(docNumberFormat === 'prefix' || docNumberFormat === 'both') &&
                  prefixSuffixRow('Prefix', prefixType, setPrefixType, 'docNumberPrefix', 'e.g. INV', prefixDateFormat, setPrefixDateFormat, prefixSeparator, setPrefixSeparator)}
                {(docNumberFormat === 'suffix' || docNumberFormat === 'both') &&
                  prefixSuffixRow('Suffix', suffixType, setSuffixType, 'docNumberSuffix', 'e.g. A', suffixDateFormat, setSuffixDateFormat, suffixSeparator, setSuffixSeparator)}

                <Grid item xs={12} md={4}>
                  {textField('docNumberLength', 'Number Length *', 'Number Length', { type: 'number', value: numberLengthValue, onChange: handleNumberLengthChange, onBlur: handleSequenceOnBlur })}
                </Grid>
                <Grid item xs={12} md={4}>
                  {textField('docSequenceNumber', 'Sequence Number *', 'Sequence Number', { type: 'number', value: sequenceValue, onChange: handleSequenceOnChange })}
                </Grid>
                <Grid item xs={12} md={4}>
                  <h5 className='c-label'>Preview</h5>
                  <Box sx={{ mt: 0.5, px: 1.5, py: 1, bgcolor: '#f9fafb', border: '1px solid #dcdcdc', borderRadius: '8px' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.95rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {previewText()}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sx={{ mt: 1 }} />
                {section('Payment Settings')}
                <Grid item xs={12} md={4}>{textField('paymentDetails', 'Payment Term', 'Payment Term')}</Grid>
                <Grid item xs={12} md={4}>{textField('paymentTermDays', 'Payment Term/Expiry Days', 'Payment Term/Expiry Days', { type: 'number' })}</Grid>

                <Grid item xs={12} sx={{ mt: 1 }} />
                {section('Additional Settings')}
                <Grid item xs={12} md={6}>{textField('termsAndConditions', 'Terms & Conditions', 'Terms & Conditions', { multiline: true, rows: 3 })}</Grid>
                <Grid item xs={12} md={6}>{textField('notes', 'Notes/Disclaimer', 'Notes/Disclaimer', { multiline: true, rows: 3 })}</Grid>

                <Grid item xs={12} sx={{ mt: 1 }} />
                {section('Custom Additional Fields')}
                <Grid item xs={12} sx={{ pt: '8px !important' }}>
                  <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '12px' }}>
                    Pre-filled on every new document that uses the default template above. For Dropdown, put the choices in Value separated by commas.
                  </Typography>
                </Grid>
                {additionalFieldDefaults.map((field: any, index: number) => (
                  <React.Fragment key={index}>
                    <Grid item xs={12} md={3}>
                      <h5 className='c-label'>Field Type</h5>
                      <Select value={field.fieldType || 'input'} onChange={(event: SelectChangeEvent<any>) => updateFieldDefault(index, 'fieldType', event.target.value)} size='small' sx={selectSx}>
                        <MenuItem value='input'>Input</MenuItem>
                        <MenuItem value='select'>Dropdown</MenuItem>
                      </Select>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <h5 className='c-label'>Label</h5>
                      <TextField placeholder='Field label' className='c-text-field-4' variant="standard" size='small' sx={inputSx}
                        value={field.label || ''} onChange={(event) => updateFieldDefault(index, 'label', event.target.value)}
                        InputProps={{ disableUnderline: true }}
                      />
                    </Grid>
                    <Grid item xs={11} md={4}>
                      <h5 className='c-label'>{field.fieldType === 'select' ? 'Options (comma separated)' : 'Value'}</h5>
                      <TextField placeholder={field.fieldType === 'select' ? 'Morning, Afternoon, Evening' : 'Optional default value'} className='c-text-field-4' variant="standard" size='small' sx={inputSx}
                        value={field.value || ''} onChange={(event) => updateFieldDefault(index, 'value', event.target.value)}
                        InputProps={{ disableUnderline: true }}
                      />
                    </Grid>
                    <Grid item xs={1} sx={{ display: 'flex', alignItems: 'flex-end', pb: 1 }}>
                      <HighlightOffTwoToneIcon
                        onClick={() => setAdditionalFieldDefaults(additionalFieldDefaults.filter((_: any, i: number) => i !== index))}
                        sx={{ cursor: 'pointer', color: '#9da7b7', fontSize: 22 }}
                      />
                    </Grid>
                  </React.Fragment>
                ))}
                <Grid item xs={12} sx={{ pt: '8px !important' }}>
                  <Button variant="outlined" size="small" sx={{ textTransform: 'none' }} startIcon={<AddCircleOutlineOutlinedIcon />}
                    onClick={() => setAdditionalFieldDefaults([...additionalFieldDefaults, { fieldType: 'input', label: '', value: '' }])}>
                    Add Field
                  </Button>
                </Grid>

                <Grid item xs={12} sx={{ mt: 1 }} />
                {section('Footer Settings')}
                <Grid item xs={12} md={6}>{textField('footer1', 'Footer 1 *', 'Footer 1', { multiline: true, rows: 2 })}</Grid>
                <Grid item xs={12} md={6}>{textField('footer2', 'Footer 2 *', 'Footer 2', { multiline: true, rows: 2 })}</Grid>

                <Grid item xs={12} sx={{ mt: 1 }} />
                {section('Communication Settings')}
                <Grid item xs={12} md={4}>{textField('emailSubject', 'Email Subject', 'Email Subject', { multiline: true, rows: 3 })}</Grid>
                <Grid item xs={12} md={4}>{textField('emailText', 'Email Text', 'Email Text', { multiline: true, rows: 3 })}</Grid>
                <Grid item xs={12} md={4}>{textField('smsAndWhatsAppText', 'SMS & WhatsApp Text', 'SMS & WhatsApp Text', { multiline: true, rows: 3 })}</Grid>

                <Grid item xs={12} sx={{ mt: 1 }} />
                {section('Tag Settings')}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Checkbox checked={tagsMandatory} onChange={(e) => setTagsMandatory(e.target.checked)} color="primary" size="small" />}
                    label={<Typography variant="body2">Tag is mandatory (this voucher can't be saved without selecting a tag)</Typography>}
                  />
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions sx={{ justifyContent: 'flex-end' }}>
              <Button variant="outlined" onClick={handleCancel}>
                Cancel
              </Button>
              <LoadingButton
                type="submit"
                name={'submit'}
                loading={status === ApiStatus.LOADING}
                variant="contained"
              >
                Save
              </LoadingButton>
            </DialogActions>
          </Box>
        )}
      </FormProvider>
    </Dialog>
  );
};

export default DialogDocumentSettingForm;
